import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/formatters';
import { apiFetch } from '../utils/api';
import PaginationFooter from './PaginationFooter';
import { useI18n } from '../hooks/useI18n';

interface PaymentInstrument {
    id: string;
    name: string;
    account_number?: string;
    company?: {
        id: string;
        name: string;
    };
}

interface Invoice {
    id: string;
    invoice_number?: string;
    vendor_name?: string;
    customer_name?: string;
    cost_center_name?: string;
    total_amount: number;
    due_date?: string | null;
    invoice_date?: string | null;
    payment_status: string;
    nav_direction: string;
}

interface BankTransaction {
    id: string;
    amount: number;
    currency: string;
    transaction_date: string;
    description?: string | null;
    payment_instrument?: PaymentInstrument;
    reconciliation_status: string;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

interface BankReconcileModalProps {
    isOpen: boolean;
    onClose: () => void;
    transaction: BankTransaction;
    onReconcileSuccess: () => void;
}

export default function BankReconcileModal({
    isOpen,
    onClose,
    transaction,
    onReconcileSuccess
}: BankReconcileModalProps) {
    const { t } = useI18n();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [reconciling, setReconciling] = useState<boolean>(false);
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setDebouncedSearch('');
            setSelectedInvoiceIds(new Set());
            fetchCandidateInvoices();
        }
    }, [isOpen]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen && !reconciling) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose, reconciling]);

    const fetchCandidateInvoices = async () => {
        setLoading(true);
        try {
            const response = await apiFetch(`/api/payments/bank-transactions/${transaction.id}/candidate-invoices`);

            if (response.ok) {
                const data = await response.json();
                setInvoices(data || []);
            } else {
                console.error('Failed to load candidate invoices:', response.status);
                const errorData = await response.json();
                console.error('Error details:', errorData);
            }
        } catch (error) {
            console.error('Error loading candidate invoices:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (invoiceId: string, checked: boolean) => {
        const newSelected = new Set(selectedInvoiceIds);
        if (checked) {
            newSelected.add(invoiceId);
        } else {
            newSelected.delete(invoiceId);
        }
        setSelectedInvoiceIds(newSelected);
    };

    const handleSingleMatch = async (invoiceId: string) => {
        if (!confirm('Are you sure you want to reconcile this transaction with the selected invoice?')) {
            return;
        }

        setReconciling(true);
        try {
            const response = await apiFetch(`/api/payments/bank-transactions/${transaction.id}/reconcile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    invoice_ids: [invoiceId],
                }),
            });

            if (response.ok) {
                onReconcileSuccess();
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to reconcile transaction');
            }
        } catch (error) {
            console.error('Error reconciling transaction:', error);
            alert('An error occurred while reconciling');
        } finally {
            setReconciling(false);
        }
    };

    const handleMultiMatch = async () => {
        if (selectedInvoiceIds.size === 0) {
            return;
        }

        if (!confirm(`Are you sure you want to reconcile this transaction with ${selectedInvoiceIds.size} invoice(s)?`)) {
            return;
        }

        setReconciling(true);
        try {
            const response = await apiFetch(`/api/payments/bank-transactions/${transaction.id}/reconcile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    invoice_ids: Array.from(selectedInvoiceIds),
                }),
            });

            if (response.ok) {
                onReconcileSuccess();
            } else {
                const errorData = await response.json();
                alert(errorData.detail || 'Failed to reconcile transaction');
            }
        } catch (error) {
            console.error('Error reconciling transaction:', error);
            alert('An error occurred while reconciling');
        } finally {
            setReconciling(false);
        }
    };

    // Calculate selected total and tolerance
    const selectedInvoices = invoices.filter(inv => selectedInvoiceIds.has(inv.id));
    const selectedTotal = selectedInvoices.reduce((sum, inv) => sum + Number(inv.total_amount), 0);
    const transactionAbs = Math.abs(transaction.amount);
    const difference = Math.abs(selectedTotal - transactionAbs);
    const tolerancePercent = transactionAbs > 0 ? (difference / transactionAbs) * 100 : 0;
    const isWithinTolerance = tolerancePercent <= 3.0;
    const exceedsTransaction = selectedTotal > transactionAbs;

    // Filter invoices by search term
    const filteredInvoices = invoices.filter(invoice => {
        if (!debouncedSearch) return true;
        const searchLower = debouncedSearch.toLowerCase();
        return (
            invoice.invoice_number?.toLowerCase().includes(searchLower) ||
            invoice.vendor_name?.toLowerCase().includes(searchLower) ||
            invoice.customer_name?.toLowerCase().includes(searchLower)
        );
    });

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={onClose}
        >
            <div
                className="bg-background border rounded-lg shadow-lg max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h2 className="text-lg font-semibold">{t('banking.reconcileTransaction', 'common')}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('banking.selectInvoices', 'common')}
                        </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={reconciling}>
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left side - Invoice list */}
                    <div className="w-2/3 flex flex-col border-r">
                        {/* Search */}
                        <div className="p-4 border-b">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                                <Input
                                    placeholder={t('banking.searchInvoices', 'common')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        {/* Invoice list */}
                        <div className="flex-1 overflow-auto">
                            {loading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                                    <span>{t('actions.loading', 'common')}</span>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12"></TableHead>
                                            <TableHead>{t('invoices.invoiceNumber', 'invoices')}</TableHead>
                                            <TableHead>{t('banking.vendorCustomer', 'common')}</TableHead>
                                            <TableHead>{t('invoices.dueDate', 'invoices')}</TableHead>
                                            <TableHead className="text-right">{t('units.amount', 'common')}</TableHead>
                                            <TableHead className="text-right">{t('actions.actions', 'common')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredInvoices.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                    {debouncedSearch ? t('banking.noMatchingInvoices', 'common') : 'No candidate invoices found'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredInvoices.map((invoice) => {
                                                const isSelected = selectedInvoiceIds.has(invoice.id);
                                                const singleMatchWithinTolerance = Math.abs((Number(invoice.total_amount) - transactionAbs) / transactionAbs * 100) <= 3.0;

                                                return (
                                                    <TableRow key={invoice.id} className={isSelected ? 'bg-muted/50' : ''}>
                                                        <TableCell>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onCheckedChange={(checked) => handleCheckboxChange(invoice.id, checked as boolean)}
                                                                disabled={reconciling}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            {invoice.invoice_number || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {invoice.nav_direction === 'INBOUND' ? invoice.vendor_name : invoice.customer_name || 'Unknown'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate((invoice.due_date || invoice.invoice_date) ?? '')}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {formatCurrency(invoice.total_amount, transaction.currency)}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleSingleMatch(invoice.id)}
                                                                disabled={reconciling || !singleMatchWithinTolerance}
                                                            >
                                                                {reconciling ? (
                                                                    <>
                                                                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                                        {t('banking.matching', 'common')}
                                                                    </>
                                                                ) : (
                                                                    t('banking.match', 'common')
                                                                )}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            )}
                        </div>

                        {/* Selected total footer */}
                        {selectedInvoiceIds.size > 0 && (
                            <div className="p-4 border-t bg-muted/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium">
                                            Selected: {selectedInvoiceIds.size} invoice(s)
                                        </div>
                                        <div className={`text-lg font-bold ${exceedsTransaction ? 'text-red-600' : isWithinTolerance ? 'text-green-600' : 'text-orange-600'}`}>
                                            {formatCurrency(selectedTotal, transaction.currency)} / {formatCurrency(transactionAbs, transaction.currency)}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Difference: {tolerancePercent.toFixed(2)}% {isWithinTolerance ? t('banking.withinTolerance', 'common') : t('banking.exceedsTolerance', 'common')}
                                        </div>
                                    </div>
                                    <Button
                                        onClick={handleMultiMatch}
                                        disabled={reconciling || !isWithinTolerance || selectedInvoiceIds.size === 0}
                                        size="lg"
                                    >
                                        {reconciling ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                {t('banking.matching', 'common')}
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                {t('banking.matchSelected', 'common')}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right side - Transaction details */}
                    <div className="w-1/3 flex flex-col">
                        <div className="p-4 border-b">
                            <h3 className="font-medium">{t('banking.transactionDetails', 'common')}</h3>
                        </div>
                        <div className="flex-1 p-4 overflow-auto space-y-4">
                            <div>
                                <div className="text-sm text-muted-foreground">{t('units.amount', 'common')}</div>
                                <div className={`text-2xl font-bold ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(transaction.amount, transaction.currency)}
                                </div>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground">{t('forms.date', 'common')}</div>
                                <div className="font-medium">{formatDate(transaction.transaction_date)}</div>
                            </div>
                            {transaction.payment_instrument?.company && (
                                <div>
                                    <div className="text-sm text-muted-foreground">{t('common.company', 'common')}</div>
                                    <div className="font-medium">{transaction.payment_instrument.company.name}</div>
                                </div>
                            )}
                            <div>
                                <div className="text-sm text-muted-foreground">{t('payments.paymentInstrument', 'common')}</div>
                                <div className="font-medium">{transaction.payment_instrument?.name || 'N/A'}</div>
                                {transaction.payment_instrument?.account_number && (
                                    <div className="text-xs text-muted-foreground">{transaction.payment_instrument.account_number}</div>
                                )}
                            </div>
                            {transaction.description && (
                                <div>
                                    <div className="text-sm text-muted-foreground">{t('forms.description', 'common')}</div>
                                    <div className="text-sm">{transaction.description}</div>
                                </div>
                            )}
                            <div>
                                <div className="text-sm text-muted-foreground">{t('banking.direction', 'common')}</div>
                                <Badge variant={transaction.amount >= 0 ? 'default' : 'secondary'}>
                                    {transaction.amount >= 0 ? t('banking.incomingAR', 'common') : t('banking.outgoingAP', 'common')}
                                </Badge>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
